/* eslint-disable no-nested-ternary */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import useTimer from '../../../hooks/useTimer';

import { useThemeClassName } from '../../../contexts/ThemeContext';
import StyledButton from '../../buttons/StyledButton';
import TimerPill from '../../partials/TimerPill';
import Question from '../../steps/Question';

import styles from '../../../../styles/displays/steps/step.module.scss';

const propTypes = {
    page: PropTypes.shape({}).isRequired,
    game: PropTypes.shape({}).isRequired,
    step: PropTypes.shape({}).isRequired,
    updateStep: PropTypes.func.isRequired,
    className: PropTypes.string,
};

const defaultProps = { className: null };

function QuestionStep({ page, game, updateStep, className }) {
    const themeClassName = useThemeClassName(styles);
    const { pageStep: step = null } = page || {};
    const { questions = [] } = game || {};
    const { id: stepId = null, label = null } = step || {};

    const stepParts = stepId !== null ? stepId.split('-') : null;
    const questionId = stepParts !== null && stepParts.length > 1 ? stepParts[1] : null;
    const currentQuestion = questions.find((q) => q.id === questionId) || null;
    const { startAt = null, endAt = null, canAnswer = false } = currentQuestion || {};

    const { ended, onEnded } = useTimer({ endAt });

    return (
        <div
            className={classNames([
                styles.container,
                themeClassName,
                {
                    [className]: className,
                },
            ])}
        >
            <div className={styles.header}>
                <div className={styles.left}>
                    <h1 className={styles.title}>
                        {label || (
                            <FormattedMessage
                                defaultMessage="Question #{id}"
                                description="Display title"
                                values={{ id: questionId }}
                            />
                        )}
                    </h1>
                </div>
                <div className={styles.right}>
                    {game !== null ? (
                        <>
                            <div className={styles.buttons}>
                                <StyledButton
                                    className={styles.button}
                                    onClick={() => updateStep(stepId, 'start')}
                                    disabled={canAnswer || endAt !== null}
                                >
                                    <FormattedMessage
                                        defaultMessage="Launch question"
                                        description="Button label"
                                    />
                                </StyledButton>
                                <StyledButton
                                    className={styles.button}
                                    onClick={() => updateStep(stepId, 'end')}
                                    disabled={!canAnswer || ended}
                                >
                                    <FormattedMessage
                                        defaultMessage="End question"
                                        description="Button label"
                                    />
                                </StyledButton>
                            </div>
                            <div className={styles.buttons}>
                                <StyledButton
                                    className={classNames([styles.button, styles.cancel])}
                                    onClick={() => updateStep(stepId, 'reset')}
                                    disabled={startAt === null && endAt === null}
                                >
                                    {endAt !== null ? (
                                        <FormattedMessage
                                            defaultMessage="Restart"
                                            description="Button label"
                                        />
                                    ) : (
                                        <FormattedMessage
                                            defaultMessage="Cancel"
                                            description="Button label"
                                        />
                                    )}
                                </StyledButton>
                            </div>
                        </>
                    ) : null}
                </div>
            </div>
            <div className={styles.screen}>
                <div className={styles.center}>
                    {endAt !== null ? (
                        <TimerPill
                            date={endAt}
                            started={!ended}
                            onEnded={onEnded}
                            className={styles.timerPill}
                            timerClassName={styles.timer}
                            showDigitsOnEnded
                        />
                    ) : null}
                    <Question
                        className={styles.question}
                        question={currentQuestion}
                        isEnded={ended}
                        isShuffled={false}
                        disabled
                        isLive
                        isPreview
                        largeImages
                    />
                </div>
            </div>
        </div>
    );
}

QuestionStep.propTypes = propTypes;
QuestionStep.defaultProps = defaultProps;

export default QuestionStep;
